// On-wire envelope codec for DataChannel messages.
//
// We have two frame formats:
//
//   ┌──────┬─────────────────────────────────────────┐
//   │ 0x00 │ UTF-8 JSON                              │   ← control envelopes
//   └──────┴─────────────────────────────────────────┘
//
//   ┌──────┬──────────────┬────────┬──────────────────┐
//   │ 0x02 │ 16-byte UUID │ seq be │ chunk bytes      │   ← file-chunk
//   └──────┴──────────────┴────────┴──────────────────┘
//      tag   id (binary)   uint32   data
//
// Why two formats? File-chunk is the hot path — a 1 GB file at 16 KB chunks
// is 65,536 frames. JSON-encoding the chunk would mean base64-ing the bytes
// (~33% overhead) plus a hundred bytes of key names per frame. The binary
// frame is 21 bytes of overhead total.
//
// Control envelopes (offer/accept/decline/eof/code/ping/pause/resume/abort)
// are rare and small, so JSON is fine — debuggability wins over the few
// bytes saved.
//
// The first byte is always the discriminator. Receivers `parse()` it and
// route accordingly.

const TAG_JSON = 0x00;
const TAG_CHUNK = 0x02;
// 0x01 is reserved — it was used in an earlier draft for "JSON+binary
// payload" before we split chunk into its own tag. Don't recycle without
// a version-negotiation step.

// Encoder/decoder instances are cheap to create but cheaper to reuse.
const ENC = new TextEncoder();
const DEC = new TextDecoder('utf-8', { fatal: true });

const CONTROL_KINDS = new Set([
  'file-offer',
  'file-accept',
  'file-decline',
  'file-eof',
  'file-pause',
  'file-resume',
  'file-abort',
  'code',
  'ping',
]);

/**
 * Serialize an envelope to an ArrayBuffer.
 *
 * @param {import('./types.js').Envelope} env
 * @returns {ArrayBuffer}
 */
export function serialize(env) {
  if (!env || typeof env.kind !== 'string') {
    throw new TypeError('serialize: envelope must have a string `kind`');
  }
  if (env.kind === 'file-chunk') {
    return serializeChunk(env);
  }
  if (!CONTROL_KINDS.has(env.kind)) {
    throw new TypeError(`serialize: unknown envelope kind ${env.kind}`);
  }
  return serializeJson(env);
}

function serializeJson(env) {
  const json = JSON.stringify(env);
  const body = ENC.encode(json);
  const out = new Uint8Array(1 + body.length);
  out[0] = TAG_JSON;
  out.set(body, 1);
  // Return an ArrayBuffer (not Uint8Array) so DataChannel.send treats it as
  // a binary frame across all browsers consistently.
  return out.buffer.slice(out.byteOffset, out.byteOffset + out.byteLength);
}

function serializeChunk(env) {
  const { id, seq, data } = env;
  if (typeof id !== 'string') {
    throw new TypeError('file-chunk: id must be string');
  }
  if (!Number.isInteger(seq) || seq < 0 || seq > 0xffffffff) {
    throw new TypeError('file-chunk: seq must be uint32');
  }
  const idBytes = uuidToBytes(id);
  const dataBytes =
    data instanceof Uint8Array
      ? data
      : data instanceof ArrayBuffer
        ? new Uint8Array(data)
        : null;
  if (!dataBytes) {
    throw new TypeError('file-chunk: data must be Uint8Array or ArrayBuffer');
  }

  const out = new Uint8Array(1 + 16 + 4 + dataBytes.length);
  out[0] = TAG_CHUNK;
  out.set(idBytes, 1);
  // seq big-endian uint32 at offset 17
  out[17] = (seq >>> 24) & 0xff;
  out[18] = (seq >>> 16) & 0xff;
  out[19] = (seq >>> 8) & 0xff;
  out[20] = seq & 0xff;
  out.set(dataBytes, 21);
  return out.buffer.slice(out.byteOffset, out.byteOffset + out.byteLength);
}

/**
 * Parse a DataChannel message into an envelope.
 *
 * @param {ArrayBuffer | Uint8Array | string} data  DataChannel.onmessage payload
 * @returns {import('./types.js').Envelope}
 */
export function parse(data) {
  // Strings should never reach us under the binary protocol, but tolerate
  // legacy senders that called channel.send(JSON.stringify(env)) directly.
  if (typeof data === 'string') {
    const env = JSON.parse(data);
    validateKind(env);
    return env;
  }

  const bytes =
    data instanceof Uint8Array
      ? data
      : data instanceof ArrayBuffer
        ? new Uint8Array(data)
        : null;
  if (!bytes || bytes.length < 1) {
    throw new TypeError('parse: empty or non-binary payload');
  }

  const tag = bytes[0];
  if (tag === TAG_JSON) {
    const body = bytes.subarray(1);
    const json = DEC.decode(body);
    const env = JSON.parse(json);
    validateKind(env);
    return env;
  }
  if (tag === TAG_CHUNK) {
    if (bytes.length < 1 + 16 + 4) {
      throw new TypeError('parse: file-chunk frame too short');
    }
    const id = bytesToUuid(bytes.subarray(1, 17));
    // Bitwise OR is signed-32 in JS — coerce to unsigned with the final >>> 0.
    const seq =
      ((bytes[17] << 24) |
        (bytes[18] << 16) |
        (bytes[19] << 8) |
        bytes[20]) >>>
      0;
    // Copy into a fresh ArrayBuffer so the caller can hold onto it without
    // pinning the receiver's potentially-larger backing buffer.
    const data = bytes.slice(21);
    return { kind: 'file-chunk', id, seq, data };
  }
  throw new TypeError(`parse: unknown frame tag 0x${tag.toString(16)}`);
}

function validateKind(env) {
  if (!env || typeof env.kind !== 'string') {
    throw new TypeError('parse: envelope missing string `kind`');
  }
  if (env.kind === 'file-chunk') {
    // file-chunk over the JSON path is allowed (some tests/replays do this)
    // but it means `data` arrived as a plain object, not bytes. Caller must
    // re-serialize before using. We don't normalize here.
    return;
  }
  if (!CONTROL_KINDS.has(env.kind)) {
    throw new TypeError(`parse: unknown envelope kind ${env.kind}`);
  }
}

// ── UUID <-> 16 bytes (compact wire format for file-chunk id) ───────────────
// Standard 8-4-4-4-12 hex with dashes, little-endian-agnostic since we just
// pack the hex bytes directly. Round-trip is exact.

function uuidToBytes(uuid) {
  if (typeof uuid !== 'string' || uuid.length !== 36) {
    throw new TypeError(`uuidToBytes: invalid UUID ${uuid}`);
  }
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) {
    throw new TypeError(`uuidToBytes: invalid UUID ${uuid}`);
  }
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}

function bytesToUuid(bytes) {
  const hex = [];
  for (let i = 0; i < 16; i++) {
    hex.push(bytes[i].toString(16).padStart(2, '0'));
  }
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  );
}

export const __test__ = { uuidToBytes, bytesToUuid, TAG_JSON, TAG_CHUNK };
